import { useCallback, useEffect, useRef, useState } from 'react';

import { pdfConfirmProvider } from '../../providers/pdfConfirmProvider';
import type { PdfConfirmRequest, PdfConfirmResult } from '../../types/pdfPreview';
import type { ResponseType } from '../../types/responses';
import {
  clearPendingPdfConfirm,
  hasPendingPdfConfirmRecord,
  newPdfConfirmKey,
  readPendingPdfConfirm,
  savePendingPdfConfirm,
} from './pdfConfirmRecovery';
import type { PendingPdfConfirm } from './pdfConfirmRecovery';

type Phase = 'idle' | 'submitting' | 'processing' | 'unknown' | 'failed' | 'completed';
const maxAutomaticChecks = 4;
const maxAutomaticLookups = 3;
const maxReplays = 3;

const errorMessages: Record<string, string> = {
  pdf_import_disabled: 'PDF import is unavailable for this account.',
  pdf_confirm_forbidden: 'You do not have permission to confirm this PDF import.',
  pdf_confirm_preview_unavailable:
    'This preview is expired or was replaced. Check your listings before trying again.',
  pdf_confirm_partial_ack_required: 'Acknowledge the incomplete PDF analysis before confirming.',
  pdf_confirm_replacement_required:
    'The unfinished listing changed. Check its current state before continuing.',
  pdf_confirm_in_progress_changed:
    'The unfinished listing changed. Check its current state before continuing.',
  pdf_confirm_multiple_editors: 'Only one listing can open in the Editor.',
  pdf_confirm_invalid_request: 'The confirmation request is invalid. Review your selections.',
  pdf_confirm_invalid_fields: 'Review the listing fields and try again.',
  pdf_confirm_invalid_media: 'Review the selected photos and try again.',
  pdf_confirm_invalid_location:
    'The selected location is no longer available. Review it before continuing.',
  pdf_confirm_invalid_replacement:
    'The unfinished listing consent is invalid. Check it again before confirming.',
  pdf_confirm_duplicate_media: 'A photo was selected twice for one listing.',
  pdf_confirm_duplicate_unit: 'A listing was included twice in the confirmation.',
  pdf_confirm_duplicate_project: 'A project was included twice in the confirmation.',
  pdf_confirm_media_invalid: 'Selected photos are no longer available for confirmation.',
  pdf_confirm_unit_membership: 'The preview listings changed. Refresh the preview.',
  pdf_confirm_project_membership: 'The preview projects changed. Refresh the preview.',
  pdf_confirm_idempotency_conflict:
    'This confirmation key is linked to different data. Check your listings before trying again.',
  pdf_confirm_already_started:
    'A confirmation is already in progress. Check its status before trying again.',
  pdf_confirm_source_invalid: 'The PDF source is no longer available for confirmation.',
  pdf_confirm_commit_failed: 'Confirmation failed while saving the imported listings.',
  pdf_confirm_preview_invalid: 'The PDF preview cannot be confirmed.',
  pdf_confirm_not_found: 'This confirmation could not be found for your account.',
  pdf_confirm_invalid_key:
    'The saved confirmation key is invalid. Check your listings before trying again.',
};

export const pdfConfirmErrorMessage = (code?: string): string =>
  code && errorMessages[code]
    ? errorMessages[code]
    : code && /^pdf_confirm_[a-z_]+$/.test(code)
      ? `Confirmation could not continue (${code}).`
      : 'Confirmation status could not be verified. Please check your listings before trying again.';

const correctableCodes = new Set([
  'pdf_confirm_invalid_request',
  'pdf_confirm_invalid_fields',
  'pdf_confirm_invalid_media',
  'pdf_confirm_duplicate_media',
  'pdf_confirm_duplicate_unit',
  'pdf_confirm_duplicate_project',
  'pdf_confirm_multiple_editors',
  'pdf_confirm_unit_membership',
  'pdf_confirm_project_membership',
  'pdf_confirm_partial_ack_required',
]);

const validCompletedResult = (response: PdfConfirmResult): boolean => {
  if (
    !Array.isArray(response.units) ||
    !Array.isArray(response.createdDraftIds) ||
    !Array.isArray(response.skippedUnitKeys)
  )
    return false;
  const seen = new Set<string>();
  for (const unit of response.units) {
    if (
      !unit ||
      typeof unit.unitKey !== 'string' ||
      seen.has(unit.unitKey) ||
      !['editor', 'draft', 'skip'].includes(unit.action) ||
      !Number.isInteger(unit.mediaCount) ||
      unit.mediaCount < 0 ||
      (unit.action === 'skip' ? unit.propertyAdId !== null : typeof unit.propertyAdId !== 'string')
    )
      return false;
    seen.add(unit.unitKey);
  }
  const editors = response.units.filter((unit) => unit.action === 'editor');
  if (editors.length > 1) return false;
  const editor = editors[0];
  if (response.editorPropertyAdId !== (editor?.propertyAdId ?? null)) return false;
  const drafts = response.units
    .filter((unit) => unit.action === 'draft')
    .map((unit) => unit.propertyAdId);
  const skips = response.units.filter((unit) => unit.action === 'skip').map((unit) => unit.unitKey);
  return (
    response.createdDraftIds.length === drafts.length &&
    response.createdDraftIds.every((id) => typeof id === 'string' && drafts.includes(id)) &&
    response.skippedUnitKeys.length === skips.length &&
    response.skippedUnitKeys.every((key) => typeof key === 'string' && skips.includes(key))
  );
};

export const usePdfConfirmOperation = (previewId?: string) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<PdfConfirmResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingPdfConfirm | null>(null);
  const pendingRef = useRef<PendingPdfConfirm | null>(null);
  const lockRef = useRef(false);
  const activeRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkCountRef = useRef(0);
  const lookupCountRef = useRef(0);
  const replayCountRef = useRef(0);
  const generationRef = useRef(0);
  const completedRef = useRef(false);
  const invalidRecoveryRef = useRef(false);

  const updatePending = useCallback((operation: PendingPdfConfirm) => {
    if (!activeRef.current) {
      const stored = readPendingPdfConfirm(operation.previewId);
      if (!stored || stored.idempotencyKey !== operation.idempotencyKey) return;
    }
    pendingRef.current = operation;
    try {
      savePendingPdfConfirm(operation);
    } catch {
      if (activeRef.current)
        setError('Recovery data could not be updated in this browser. Keep this page open.');
    }
    if (activeRef.current) setPending(operation);
  }, []);

  const acceptResult = useCallback(
    (response: PdfConfirmResult, operation: PendingPdfConfirm) => {
      if (completedRef.current) return false;
      if (
        response.previewId !== operation.previewId ||
        typeof response.confirmId !== 'string' ||
        !response.confirmId ||
        (operation.confirmId && response.confirmId !== operation.confirmId)
      ) {
        if (activeRef.current) {
          setPhase('unknown');
          setError(
            'Confirmation returned an inconsistent result. Check your listings before trying again.'
          );
        }
        return false;
      }
      const updated = { ...operation, confirmId: response.confirmId };
      updatePending(updated);
      if (
        !['processing', 'completed', 'cleanup_pending', 'failed'].includes(response.status) ||
        (response.status === 'completed' && !validCompletedResult(response))
      ) {
        if (activeRef.current) {
          setPhase('unknown');
          setError(
            'Confirmation returned an incomplete result. Check its status before continuing.'
          );
        }
        return false;
      }
      if (response.status === 'completed') {
        completedRef.current = true;
        if (timerRef.current) clearTimeout(timerRef.current);
        if (activeRef.current) {
          try {
            clearPendingPdfConfirm(operation.previewId);
          } catch {
            /* Session storage may be unavailable after completion. */
          }
          pendingRef.current = null;
          setPending(null);
          setResult(response);
          setPhase('completed');
          setError(null);
        }
      } else if (response.status === 'failed' || response.status === 'cleanup_pending') {
        if (activeRef.current) {
          setPhase('failed');
          setError(
            response.errorCode
              ? pdfConfirmErrorMessage(response.errorCode)
              : response.status === 'cleanup_pending'
                ? 'Confirmation failed and cleanup is pending. Check its status before trying again.'
                : 'Confirmation failed. Check your listings before trying again.'
          );
        }
      } else if (activeRef.current) {
        setPhase('processing');
        setError(null);
      }
      return true;
    },
    [updatePending]
  );

  const checkStatus = useCallback(
    async (automatic = false) => {
      const operation = pendingRef.current;
      if (!operation?.confirmId || lockRef.current) return;
      const generation = generationRef.current;
      lockRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      try {
        const response = await pdfConfirmProvider.getStatus(operation.confirmId);
        if (generation !== generationRef.current) return;
        if (response.ok && response.data) {
          const accepted = acceptResult(response.data, operation);
          if (
            accepted &&
            response.data.status === 'processing' &&
            automatic &&
            checkCountRef.current < maxAutomaticChecks &&
            activeRef.current
          ) {
            checkCountRef.current += 1;
            timerRef.current = setTimeout(() => void checkStatus(true), 2000);
          }
        } else if (activeRef.current) {
          setPhase('unknown');
          setError(pdfConfirmErrorMessage(response.code));
        }
      } catch {
        if (generation !== generationRef.current) return;
        if (activeRef.current) {
          setPhase('unknown');
          setError(pdfConfirmErrorMessage());
        }
      } finally {
        if (generation === generationRef.current) lockRef.current = false;
      }
    },
    [acceptResult]
  );

  const lookup = useCallback(
    async (automatic = false) => {
      const operation = pendingRef.current;
      if (!operation || operation.confirmId || lockRef.current || completedRef.current) return;
      const generation = generationRef.current;
      lockRef.current = true;
      if (!automatic) lookupCountRef.current = 0;
      lookupCountRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      if (activeRef.current) {
        setPhase('unknown');
        setError(null);
      }
      try {
        const response = await pdfConfirmProvider.lookupPdfConfirm(
          operation.previewId,
          operation.idempotencyKey
        );
        if (generation !== generationRef.current || !activeRef.current) return;
        if (response.ok && response.data) {
          const accepted = acceptResult(response.data, operation);
          if (accepted && response.data.status === 'processing') {
            checkCountRef.current = 0;
            timerRef.current = setTimeout(() => void checkStatus(true), 2000);
          }
        } else {
          setPhase('unknown');
          const notFound = response.code === 'pdf_confirm_not_found' || response.status === 404;
          setError(
            response.code === 'pdf_confirm_invalid_key'
              ? pdfConfirmErrorMessage(response.code)
              : response.status === 401
                ? 'Sign in to check this confirmation.'
                : response.status === 403
                  ? 'You do not have permission to check this confirmation.'
                  : notFound
                    ? 'Confirmation has not been found yet. Its result is still unknown; check your listings before trying again.'
                    : pdfConfirmErrorMessage(response.code)
          );
          if (notFound && lookupCountRef.current < maxAutomaticLookups) {
            timerRef.current = setTimeout(() => void lookup(true), 2000);
          }
        }
      } catch {
        if (generation !== generationRef.current || !activeRef.current) return;
        setPhase('unknown');
        setError(pdfConfirmErrorMessage());
      } finally {
        if (generation === generationRef.current) lockRef.current = false;
      }
    },
    [acceptResult, checkStatus]
  );

  useEffect(() => {
    activeRef.current = true;
    generationRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
    lockRef.current = false;
    checkCountRef.current = 0;
    lookupCountRef.current = 0;
    replayCountRef.current = 0;
    completedRef.current = false;
    invalidRecoveryRef.current = false;
    setPending(null);
    setResult(null);
    setError(null);
    setPhase('idle');
    if (previewId) {
      const existing = readPendingPdfConfirm(previewId);
      if (existing) {
        pendingRef.current = existing;
        setPending(existing);
        setPhase(existing.confirmId ? 'processing' : 'unknown');
        if (existing.confirmId) void checkStatus(true);
        else void lookup(true);
      } else if (hasPendingPdfConfirmRecord(previewId)) {
        invalidRecoveryRef.current = true;
        setPhase('unknown');
        setError(
          'Saved confirmation data is invalid or unavailable. Check your listings before trying again.'
        );
      }
    }
    return () => {
      activeRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [previewId, checkStatus, lookup]);

  const send = useCallback(
    async (operation: PendingPdfConfirm) => {
      if (lockRef.current) return;
      const generation = generationRef.current;
      lockRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      if (activeRef.current) {
        setPhase('submitting');
        setError(null);
      }
      let response: ResponseType<PdfConfirmResult>;
      try {
        response = await pdfConfirmProvider.confirm(
          operation.previewId,
          operation.request,
          operation.idempotencyKey
        );
      } catch {
        response = { ok: false, data: null, message: 'Network request failed' };
      }
      let shouldLookup = false;
      try {
        if (generation !== generationRef.current) return;
        if (response.ok && response.data) {
          const accepted = acceptResult(response.data, operation);
          if (
            accepted &&
            response.data.status === 'processing' &&
            activeRef.current &&
            checkCountRef.current < maxAutomaticChecks
          ) {
            checkCountRef.current += 1;
            timerRef.current = setTimeout(() => void checkStatus(true), 2000);
          }
        } else if (response.code && correctableCodes.has(response.code) && !operation.confirmId) {
          clearPendingPdfConfirm(operation.previewId);
          pendingRef.current = null;
          if (activeRef.current) {
            setPending(null);
            setPhase('failed');
            setError(pdfConfirmErrorMessage(response.code));
          }
        } else if (activeRef.current) {
          setPhase('unknown');
          setError(pdfConfirmErrorMessage(response.code));
          shouldLookup = !operation.confirmId;
        }
      } finally {
        if (generation === generationRef.current) lockRef.current = false;
      }
      if (shouldLookup && activeRef.current && generation === generationRef.current)
        await lookup(true);
    },
    [acceptResult, checkStatus, lookup]
  );

  const start = useCallback(
    async (request: PdfConfirmRequest): Promise<void> => {
      if (
        !previewId ||
        pendingRef.current ||
        lockRef.current ||
        result ||
        invalidRecoveryRef.current
      )
        return;
      let operation: PendingPdfConfirm;
      try {
        operation = {
          previewId,
          idempotencyKey: newPdfConfirmKey(),
          request,
          confirmId: null,
        };
        savePendingPdfConfirm(operation);
      } catch {
        if (activeRef.current) {
          setPhase('failed');
          setError('Safe confirmation recovery could not be prepared in this browser.');
        }
        return;
      }
      pendingRef.current = operation;
      if (activeRef.current) setPending(operation);
      await send(operation);
    },
    [previewId, result, send]
  );

  const replay = useCallback(async (): Promise<void> => {
    const operation = pendingRef.current;
    if (
      !operation?.confirmId ||
      phase !== 'processing' ||
      lockRef.current ||
      replayCountRef.current >= maxReplays
    )
      return;
    replayCountRef.current += 1;
    checkCountRef.current = 0;
    await send(operation);
  }, [phase, send]);

  return {
    phase,
    result,
    error,
    pending,
    start,
    replay,
    checkStatus,
    lookup,
    replayAvailable:
      phase === 'processing' && Boolean(pending?.confirmId) && replayCountRef.current < maxReplays,
  };
};
